import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { formatHeat, formatShortDate, getCategoryLabel, heatPercent } from "@/lib/site";
import StatusTag from "@/components/feature/StatusTag";

interface TopicCardProps {
  topic: Topic;
  variant?: "default" | "wide";
}

export default function TopicCard({ topic, variant = "default" }: TopicCardProps) {
  const categoryLabel = getCategoryLabel(topic.categoryId);
  const percent = heatPercent(topic.heat);

  if (variant === "wide") {
    return (
      <article className="group grid overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:grid-cols-[240px_1fr]">
        <Link
          to={`/topic/${topic.id}`}
          className="relative block h-44 w-full overflow-hidden sm:h-full"
          aria-label={topic.title}
        >
          <img
            src={topic.cover}
            alt={`${topic.title} 话题配图`}
            title={`${topic.title} - 进展追踪`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col p-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-secondary-200 bg-secondary-50 px-2 py-[2px] text-[11px] font-medium text-secondary-800">
              {categoryLabel}
            </span>
            <StatusTag label={topic.status} tone={topic.statusTone} />
          </div>
          <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
            <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {topic.summary}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              更新于 {formatShortDate(topic.updatedAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-file-list-3-line" aria-hidden="true" />
              {topic.timeline.length} 个节点
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              热度 {formatHeat(topic.heat)}
            </span>
          </div>
          <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-background-200">
            <span
              className="block h-full rounded-full bg-primary-500"
              style={{ width: `${percent}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/topic/${topic.id}`}
        className="relative block h-48 w-full overflow-hidden"
        aria-label={topic.title}
      >
        <img
          src={topic.cover}
          alt={`${topic.title} 话题配图`}
          title={`${topic.title} - 进展追踪`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2 py-[3px] text-[11px] font-medium text-foreground-800">
          {categoryLabel}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <StatusTag label={topic.status} tone={topic.statusTone} />
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
          {topic.summary}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true" />
            {formatShortDate(topic.updatedAt)} 更新
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            {formatHeat(topic.heat)}
          </span>
        </div>
      </div>
    </article>
  );
}